import api from './api';
import { DocumentosKpis, normalizeDocumentosKpis } from '../utils/documentosKpis';
import { umDesdeDetalleTransaccion } from '../utils/umVentaDocumento';
import { normalizeUmSnapshotFacFromApi } from '../utils/umDocumentoDisplay';

export interface NotaDebitoVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioUnitario?: number;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    notas?: string;
    um?: string;
    /** Snapshot Fac: cantidad ingresada por el usuario. */
    cantidadUmfac?: number;
    /** Snapshot Fac: UM ingresada. */
    umfac?: string;
    /** Snapshot Fac: factor hacia UM base. */
    equivUmfac?: number;
    /** Snapshot Fac: precio unitario ingresado (en umfac). */
    precioUnitarioUmfac?: number;
    codigoMoneda?: number;
    cotizacion?: number;
}

export interface NotaDebitoVenta {
    nroFormulario: number;
    nroTipoFormulario: string;
    tipoCliente: string;
    fechaEmision: Date | string;
    horaEmision?: Date | string;
    nroCliente: number;
    nombre: string;
    direccionCliente?: string | null;
    direccionEnvio?: string | null;
    nroVendedor?: number;
    nroLista?: string;
    codigoMoneda: string | number;
    importeTotal: number;
    asentado?: string;
    descripcion?: string;
    usuario?: string;
    detalles?: NotaDebitoVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    facturado?: string;
    nroFormularioFactura?: number;
    integrar?: number;
    estado?: string;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
    cuit?: string;
    idCompania?: string;
    caeNro?: string;
    caeVencimiento?: string | null;
    /** Asiento de libro diario vinculado al comprobante, si existe. */
    nroAsiento?: number | null;
}

export interface NotaDebitoVentaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date;
    fechaHasta?: Date;
    nroCliente?: string;
    nroVendedor?: string;
    asentado?: string;
    estado?: string;
    nroSuc?: number;
}

export interface NotaDebitoVentaPagedResult {
    items: NotaDebitoVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Item para crear una Nota de Débito (POST api/NotasDebitoVenta) */
export interface NotaDebitoItemCreateDto {
    codigoArticulo: string;
    descripcion?: string;
    cantidad: number;
    precioUnitario: number;
    /** Porcentaje de descuento del renglón (0-100). precioUnitario = precio de lista. */
    porcDesc?: number;
    /** Seguimiento: formulario de la línea origen (factura, pedido, etc.). */
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    um?: string;
    codigoMoneda?: number;
    cotizacion?: number;
    notas?: string;
    lotes?: import('./loteService').LoteAsignacion[];
}

/** Formulario encabezado para crear una Nota de Débito */
export interface NotaDebitoFormularioCreateDto {
    idCompania: string;
    descripcion?: string;
    nroSuc?: number;
    fechaEmision?: string;
    fechaVencim?: string;
    nroCliente: number;
    nombre?: string;
    nroLista?: string;
    formaDePago?: string;
    nroUbicacionOrigen?: number;
    ordenDeCompra?: string;
    codigoDireccion?: string;
    codigoDireccionEnvio?: string;
    formaEntrega?: string;
    nroFormularioFactura?: number;
    /** Si se informa y es > 0, se usa en el comprobante; si no, el backend toma el vendedor del cliente. */
    nroVendedor?: number;
    /** CUIT o documento del receptor en el comprobante. */
    cuit?: string;
    /** Condición IVA del receptor (I, M, C, …). */
    tipoIva?: string;
}

/** Payload para POST api/NotasDebitoVenta */
export interface NotaDebitoCreateDto {
    formulario: NotaDebitoFormularioCreateDto;
    items: NotaDebitoItemCreateDto[];
}

const notaDebitoVentaService = {
    getNotasDebitoVenta: async (params: NotaDebitoVentaSearchParams): Promise<NotaDebitoVentaPagedResult> => {
        const queryParams: Record<string, string | number> = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
        };
        if (params.searchTerm) queryParams.searchTerm = params.searchTerm;
        if (params.fechaDesde) queryParams.fechaDesde = params.fechaDesde.toISOString().split('T')[0];
        if (params.fechaHasta) queryParams.fechaHasta = params.fechaHasta.toISOString().split('T')[0];
        if (params.nroCliente) queryParams.nroCliente = params.nroCliente;
        if (params.nroVendedor) queryParams.nroVendedor = params.nroVendedor;
        if (params.asentado) queryParams.asentado = params.asentado;
        if (params.estado) queryParams.estado = params.estado;
        if (params.nroSuc != null && params.nroSuc > 0) queryParams.NroSuc = params.nroSuc;

        const response = await api.get<NotaDebitoVentaPagedResult>('/api/NotasDebitoVenta', { params: queryParams });
        return response.data;
    },

    getNotasDebitoVentaKpis: async (
        params: Omit<NotaDebitoVentaSearchParams, 'pageNumber' | 'pageSize'>,
    ): Promise<DocumentosKpis> => {
        const queryParams: Record<string, unknown> = {
            pageNumber: 1,
            pageSize: 1,
        };
        if (params.searchTerm) queryParams.searchTerm = params.searchTerm;
        if (params.fechaDesde) queryParams.fechaDesde = params.fechaDesde.toISOString().split('T')[0];
        if (params.fechaHasta) queryParams.fechaHasta = params.fechaHasta.toISOString().split('T')[0];
        if (params.nroCliente) queryParams.nroCliente = params.nroCliente;
        if (params.nroVendedor) queryParams.nroVendedor = params.nroVendedor;
        if (params.asentado) queryParams.asentado = params.asentado;
        if (params.estado) queryParams.estado = params.estado;
        if (params.nroSuc != null && params.nroSuc > 0) queryParams.NroSuc = params.nroSuc;
        const response = await api.get('/api/NotasDebitoVenta/kpis', {
            params: queryParams,
        });
        return normalizeDocumentosKpis(response.data);
    },

    getNotaDebitoVenta: async (id: number): Promise<NotaDebitoVenta> => {
        const response = await api.get<NotaDebitoVenta>(`/api/NotasDebitoVenta/${id}`);
        const data = response.data;
        return {
            ...data,
            detalles: (data.detalles || []).map((d) => ({
                ...d,
                um: umDesdeDetalleTransaccion(d) || undefined,
                ...normalizeUmSnapshotFacFromApi(d),
            })),
        };
    },

    getNotaDebitoVentaPDF: async (id: number, idCompania?: string): Promise<Blob> => {
        const params = idCompania?.trim()
            ? `?idCompania=${encodeURIComponent(idCompania.trim())}`
            : '';
        const response = await api.get(`/api/NotasDebitoVenta/${id}/pdf${params}`, {
            responseType: 'blob'
        });
        return response.data;
    },

    obtenerCaeArgentina: async (nroFormulario: number, idCompania?: string): Promise<NotaDebitoVenta> => {
        const params = idCompania?.trim()
            ? `?idCompania=${encodeURIComponent(idCompania.trim())}`
            : '';
        const response = await api.post<NotaDebitoVenta>(
            `/api/NotasDebitoVenta/${nroFormulario}/obtener-cae-argentina${params}`
        );
        return response.data;
    },

    createNotaDebito: async (payload: NotaDebitoCreateDto): Promise<NotaDebitoVenta> => {
        const response = await api.post<NotaDebitoVenta>('/api/NotasDebitoVenta', payload);
        return response.data;
    },

    anularNotaDebito: async (id: number): Promise<void> => {
        await api.post(`/api/NotasDebitoVenta/${id}/anular`);
    },

    /** Envía la nota de débito al email del cliente, con el PDF adjunto. */
    enviarNotaDebitoPorEmailAlCliente: async (
        nroFormulario: number
    ): Promise<{ enviado: boolean; mensaje?: string }> => {
        const response = await api.post<{ enviado: boolean; mensaje?: string }>(
            `/api/NotasDebitoVenta/${nroFormulario}/enviar-email-cliente`
        );
        return response.data;
    },

    /** Solo SUPERADMIN: borra asientos vinculados al formulario y crea uno nuevo con la lógica actual. */
    regenerarAsientoNotaDebitoVenta: async (
        id: number
    ): Promise<{ nroAsiento: number; message?: string }> => {
        const response = await api.post<{ nroAsiento: number; message?: string }>(
            `/api/NotasDebitoVenta/${id}/regenerar-asiento`
        );
        return response.data;
    },
};

export default notaDebitoVentaService;
